/**
 * Copyright © 2025 Delicious AI, LLC
 */

import { AutoAwesome, TrendingDown } from '@mui/icons-material'
import { Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/system'
import React from 'react'
import Markdown from 'react-markdown'

import { ProductNotSoldMetadataFragment } from '@/graphql'
import { formatCurrency } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import { ProductImage } from '@/pages/dashboards/Default/ProductImage'
import { MarkdownHelpers } from '@/utils/markdown.helpers'

type ProductNotSoldInsightProps = {
  metadata: ProductNotSoldMetadataFragment
}

const ProductNotSoldInsight: React.FC<ProductNotSoldInsightProps> = ({
  metadata,
}) => {
  const theme = useTheme()
  const { t } = useT()
  return (
    <Stack spacing={4}>
      <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
        <Stack direction={'row'} alignItems={'center'} spacing={2}>
          <AutoAwesome />
          <Typography variant="h5">
            {t('aiInsightsTable.productNotSold.identified')}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        pl={8}
        direction={'row'}
        alignItems={'center'}
        spacing={4}
        height={200}
      >
        <Stack spacing={2} height={'100%'}>
          <Paper
            variant={'outlined'}
            sx={{
              bgcolor: theme.palette.background.paper,
              flex: 1,
              alignContent: 'center',
            }}
          >
            <ProductImage
              upc={metadata.upc}
              width="100px"
              height="auto"
              maxHeight={'150px'}
              minHeight={'100px'}
              objectFit={'contain'}
              skeleton
            />
          </Paper>
          <Stack direction={'row'} justifyContent={'center'} spacing={1}>
            <TrendingDown color="error" />
            <Typography
              variant={'subtitle2'}
              fontWeight={'bold'}
              color="error.light"
              textAlign={'center'}
            >
              {formatCurrency(metadata.historicalRevenue, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Markdown>{MarkdownHelpers.format(metadata.message)}</Markdown>
    </Stack>
  )
}

export default ProductNotSoldInsight
